import type { FlowNode } from '../types/flowchart.types';
import type { AlignMode, DistributeAxis } from '../editor/FlowToolbar';
import { FLOW_TOKENS as T } from '../shared/flowTokens';

export function alignNodes(nodes: FlowNode[], selectedIds: string[], mode: AlignMode): FlowNode[] {
  const selected = nodes.filter((n) => selectedIds.includes(n.id));
  if (selected.length < 2) return nodes;

  let target: number;
  switch (mode) {
    case 'left':
      target = Math.min(...selected.map((n) => n.position.x));
      break;
    case 'right': {
      target = Math.max(...selected.map((n) => n.position.x + (n.width ?? T.node.width)));
      return nodes.map((n) => {
        if (!selectedIds.includes(n.id)) return n;
        const w = n.width ?? T.node.width;
        return { ...n, position: { ...n.position, x: target - w } };
      });
    }
    case 'centerH': {
      const centers = selected.map((n) => n.position.x + (n.width ?? T.node.width) / 2);
      target = centers.reduce((a, b) => a + b, 0) / centers.length;
      return nodes.map((n) => {
        if (!selectedIds.includes(n.id)) return n;
        const w = n.width ?? T.node.width;
        return { ...n, position: { ...n.position, x: target - w / 2 } };
      });
    }
    case 'top':
      target = Math.min(...selected.map((n) => n.position.y));
      break;
    case 'bottom': {
      target = Math.max(...selected.map((n) => n.position.y + (n.height ?? T.node.height)));
      return nodes.map((n) => {
        if (!selectedIds.includes(n.id)) return n;
        const h = n.height ?? T.node.height;
        return { ...n, position: { ...n.position, y: target - h } };
      });
    }
    case 'centerV': {
      const centers = selected.map((n) => n.position.y + (n.height ?? T.node.height) / 2);
      target = centers.reduce((a, b) => a + b, 0) / centers.length;
      return nodes.map((n) => {
        if (!selectedIds.includes(n.id)) return n;
        const h = n.height ?? T.node.height;
        return { ...n, position: { ...n.position, y: target - h / 2 } };
      });
    }
    default:
      return nodes;
  }

  const axis = mode === 'left' ? 'x' : 'y';
  return nodes.map((n) => {
    if (!selectedIds.includes(n.id)) return n;
    return { ...n, position: { ...n.position, [axis]: target } };
  });
}

export function distributeNodes(
  nodes: FlowNode[],
  selectedIds: string[],
  axis: DistributeAxis,
): FlowNode[] {
  const selected = nodes.filter((n) => selectedIds.includes(n.id));
  if (selected.length < 3) return nodes;

  const isH = axis === 'horizontal';
  const sorted = [...selected].sort((a, b) =>
    isH ? a.position.x - b.position.x : a.position.y - b.position.y,
  );

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const firstPos = isH ? first.position.x : first.position.y;
  const lastPos = isH
    ? last.position.x + (last.width ?? T.node.width)
    : last.position.y + (last.height ?? T.node.height);
  const totalNodeSize = sorted.reduce(
    (acc, n) => acc + (isH ? (n.width ?? T.node.width) : (n.height ?? T.node.height)),
    0,
  );
  const totalGap = (lastPos - firstPos - totalNodeSize) / (sorted.length - 1);

  let currentPos = firstPos;
  const posMap = new Map<string, number>();
  for (const n of sorted) {
    posMap.set(n.id, currentPos);
    currentPos += (isH ? (n.width ?? T.node.width) : (n.height ?? T.node.height)) + totalGap;
  }

  return nodes.map((n) => {
    const newPos = posMap.get(n.id);
    if (newPos === undefined) return n;
    return {
      ...n,
      position: {
        ...n.position,
        [isH ? 'x' : 'y']: newPos,
      },
    };
  });
}
